// A customer's quotation page (#512): the link in a quotation email opens it at `/quotation#<token>`, with no
// sign-in. It shows that quotation only, its PDF, and Accept, Ask a question and Decline; the answer goes to the
// business, who records it. The token rides in the fragment, which the browser never sends to a server, so no log or
// Referer carries it. An expired or unknown link says so in plain words.
import { useEffect, useId, useState } from "react";
import { rupees } from "../api.js";
import { useWords } from "../i18n/words.js";
import type { QuotationAnswer, QuotationLinkApi, QuotationPage } from "./api.js";

export const QUOTATION_PATH = "/quotation";

const tokenInLink = (): string => decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();

export function QuotationLink({ api }: { api: QuotationLinkApi }) {
  const w = useWords();
  const [token] = useState(tokenInLink);
  const [page, setPage] = useState<QuotationPage | null | undefined>(undefined);
  const [asking, setAsking] = useState(false);
  const [words, setWords] = useState("");
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState<QuotationAnswer | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const questionId = useId();

  useEffect(() => {
    if (token === "") {
      setPage(null);
      return;
    }
    api.open(token).then(setPage, () => setProblem(w.quotationUnreachable));
  }, [api, token]);

  const answer = async (choice: QuotationAnswer) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await api.answer(token, choice, choice === "question" ? words.trim() : undefined);
      setAnswered(choice);
    } catch {
      setProblem(w.quotationUnreachable);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    try {
      const pdf = await api.pdf(token);
      const url = URL.createObjectURL(pdf);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${page?.number.replace(/[^\w.-]+/g, "-") ?? "quotation"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setProblem(w.quotationUnreachable);
    }
  };

  return (
    <main className="sign-in" id="main">
      {page === undefined && !problem ? <p role="status">{w.quotationOpening}</p> : null}
      {page === null ? <p role="alert">{w.quotationGone}</p> : null}
      {page ? (
        <>
          <h1>{w.quotationTitle(page.number, page.business)}</h1>
          <p>{w.quotationFor(page.customer, page.date, page.validUntil)}</p>
          <table>
            <thead>
              <tr>
                <th scope="col">{w.quotationItem}</th>
                <th scope="col">{w.quotationQuantity}</th>
                <th scope="col">{w.quotationRate}</th>
                <th scope="col">{w.quotationAmount}</th>
              </tr>
            </thead>
            <tbody>
              {page.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.item}</td>
                  <td>
                    {l.quantity} {l.unit}
                  </td>
                  <td>{rupees(l.unitPrice)}</td>
                  <td>{rupees(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>{w.quotationTotal(rupees(page.total))}</p>
          {page.pdf ? (
            <p>
              <button type="button" className="btn" onClick={() => void download()}>
                {w.quotationPdf}
              </button>
            </p>
          ) : null}
          {answered ? (
            <p role="status">{w.quotationAnswered(answered, page.business)}</p>
          ) : asking ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (words.trim() !== "") void answer("question");
              }}
            >
              <label className="field" htmlFor={questionId}>
                {w.quotationQuestion}
              </label>
              <textarea
                id={questionId}
                rows={4}
                maxLength={1000}
                value={words}
                onChange={(event) => setWords(event.target.value)}
                required
              />
              <div className="acts">
                <button type="submit" className="btn btn-go" disabled={busy || words.trim() === ""}>
                  {w.quotationSend}
                </button>
                <button type="button" className="btn" disabled={busy} onClick={() => setAsking(false)}>
                  {w.quotationBack}
                </button>
              </div>
            </form>
          ) : (
            <div className="acts">
              <button type="button" className="btn btn-go" disabled={busy} onClick={() => void answer("accept")}>
                {w.quotationAccept}
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => setAsking(true)}>
                {w.quotationAsk}
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => void answer("decline")}>
                {w.quotationDecline}
              </button>
            </div>
          )}
        </>
      ) : null}
      {problem ? <p role="alert">{problem}</p> : null}
    </main>
  );
}
